import { useState, useCallback, useEffect, useRef } from 'react';
import { BleClient, BleDevice } from '@capacitor-community/bluetooth-le';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Preferences } from '@capacitor/preferences';

// Wellebit Smart Water Bottle BLE Protocol Constants
// Frame Header: 0xCB 0xBC
const FRAME_HEADER = [0xCB, 0xBC];

// CRC-16-CCITT (0x1021) polynomial
const CRC_POLYNOMIAL = 0x1021;

// Command codes based on protocol documentation
const COMMANDS = {
  // App Request Commands (odd numbers)
  REQUEST_CHANGED_WATER_VOLUME: 0x01,
  REQUEST_CURRENT_WATER_VOLUME: 0x03,
  REQUEST_STERILISATION_STATUS: 0x05,
  REQUEST_STERILISATION_DURATION: 0x07,
  REQUEST_TEMPERATURE: 0x09,
  REQUEST_DRINKING_LID_STATUS: 0x0B,
  REQUEST_REFILL_LID_STATUS: 0x0D,
  REQUEST_ORIENTATION: 0x0F,
  REQUEST_BATTERY_LEVEL: 0x11,
  REQUEST_BATTERY_FULL: 0x13,
  REQUEST_LOW_BATTERY: 0x15,
  REQUEST_SOFTWARE_VERSION: 0x17,
  SET_DRINK_REMINDER: 0x19,
  SET_SCROLLING_DISPLAY: 0x1B,
  SET_MANUAL_STERILISATION: 0x1D,
  SET_SYSTEM_TIME: 0x1F,
  SET_TIMED_VIBRATION: 0x21,
  
  // MCU Response Commands (even numbers)
  RESPONSE_CHANGED_WATER_VOLUME: 0x02,
  RESPONSE_CURRENT_WATER_VOLUME: 0x04,
  RESPONSE_STERILISATION_STATUS: 0x06,
  RESPONSE_STERILISATION_DURATION: 0x08,
  RESPONSE_TEMPERATURE: 0x0A,
  RESPONSE_DRINKING_LID_STATUS: 0x0C,
  RESPONSE_REFILL_LID_STATUS: 0x0E,
  RESPONSE_ORIENTATION: 0x10,
  RESPONSE_BATTERY_LEVEL: 0x12,
  RESPONSE_BATTERY_FULL: 0x14,
  RESPONSE_LOW_BATTERY: 0x16,
  RESPONSE_SOFTWARE_VERSION: 0x18,
  RESPONSE_DRINK_REMINDER: 0x1A,
  RESPONSE_SCROLLING_DISPLAY: 0x1C,
  RESPONSE_MANUAL_STERILISATION: 0x1E,
  RESPONSE_SYSTEM_TIME: 0x20,
  RESPONSE_TIMED_VIBRATION: 0x22,
} as const;

// BLE Service UUIDs - G1100 bottle uses custom UUIDs
// These need to be discovered from the device - placeholder values for now
// Common patterns: Nordic UART Service, or custom manufacturer UUIDs
const WELLEBIT_SERVICE_UUID = '0000fff0-0000-1000-8000-00805f9b34fb'; // Common custom service
const WELLEBIT_WRITE_CHARACTERISTIC_UUID = '0000fff1-0000-1000-8000-00805f9b34fb';
const WELLEBIT_NOTIFY_CHARACTERISTIC_UUID = '0000fff2-0000-1000-8000-00805f9b34fb';

// Alternative UUIDs to try if the above don't work
const ALTERNATE_SERVICE_UUIDS = [
  '0000fff0-0000-1000-8000-00805f9b34fb',
  '0000ffe0-0000-1000-8000-00805f9b34fb',
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service
  '0000180f-0000-1000-8000-00805f9b34fb', // Battery Service
];

export interface DiscoveredService {
  uuid: string;
  characteristics: string[];
  characteristicProperties?: Record<string, { write?: boolean; notify?: boolean; read?: boolean }>;
}

export interface BottleData {
  deviceId: string;
  deviceName: string;
  connected: boolean;
  batteryLevel: number | null; // percent (null until first real reading)
  batteryFull: boolean;
  lowBattery: boolean;
  waterTemperature: number; // °C
  currentWaterVolume: number; // 0.01L units (10ml)
  lastDrinkAmount: number; // 0.01L units
  totalIntakeToday: number;
  sterilisationOn: boolean;
  sterilisationDuration: number; // seconds
  drinkingLidOpen: boolean;
  refillLidOpen: boolean;
  isTilted: boolean;
  softwareVersion: number;
  lastSync: Date;
  // Debug: discovered BLE services (temporary for development)
  discoveredServices?: DiscoveredService[];
}

export interface HydrationEvent {
  timestamp: Date;
  amountMl: number;
  temperature: number;
}

// CRC-16-CCITT calculation (big-endian result)
function calculateCRC16(data: number[]): number {
  let crc = 0xFFFF;
  for (const byte of data) {
    crc ^= byte << 8;
    for (let i = 0; i < 8; i++) {
      if (crc & 0x8000) {
        crc = (crc << 1) ^ CRC_POLYNOMIAL;
      } else {
        crc = crc << 1;
      }
    }
    crc &= 0xFFFF;
  }
  return crc;
}

// Build a command frame according to protocol
function buildCommandFrame(commandCode: number, data: number[] = []): Uint8Array {
  const frame = [...FRAME_HEADER, commandCode, data.length, ...data];
  const crc = calculateCRC16(frame);
  // CRC is big-endian
  frame.push((crc >> 8) & 0xFF, crc & 0xFF);
  return new Uint8Array(frame);
}

// Parse a response frame
function parseResponseFrame(data: DataView): { command: number; payload: number[] } | null {
  if (data.byteLength < 6) return null; // Minimum: header(2) + cmd(1) + len(1) + crc(2)
  
  // Verify header
  if (data.getUint8(0) !== FRAME_HEADER[0] || data.getUint8(1) !== FRAME_HEADER[1]) {
    return null;
  }
  
  const command = data.getUint8(2);
  const dataLength = data.getUint8(3);
  
  if (data.byteLength < 4 + dataLength + 2) return null;
  
  const payload: number[] = [];
  for (let i = 0; i < dataLength; i++) {
    payload.push(data.getUint8(4 + i));
  }
  
  // Verify CRC (big-endian)
  const receivedCRC = (data.getUint8(4 + dataLength) << 8) | data.getUint8(5 + dataLength);
  const frameWithoutCRC = [];
  for (let i = 0; i < 4 + dataLength; i++) {
    frameWithoutCRC.push(data.getUint8(i));
  }
  const calculatedCRC = calculateCRC16(frameWithoutCRC);
  
  if (receivedCRC !== calculatedCRC) {
    console.warn('CRC mismatch:', { receivedCRC, calculatedCRC });
    return null;
  }
  
  return { command, payload };
}

// Parse little-endian 16-bit value from payload
function parseUint16LE(payload: number[], offset = 0): number {
  return payload[offset] | (payload[offset + 1] << 8);
}

function normalizeBatteryLevel(raw: number): number | null {
  if (!Number.isFinite(raw) || raw < 0) return null;
  // Some firmwares send 0-20 in 5% units, others send 0-100 directly.
  const value = raw <= 20 ? raw * 5 : raw;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function useWellebitBluetooth() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [discoveredDevices, setDiscoveredDevices] = useState<BleDevice[]>([]);
  const [connectedBottle, setConnectedBottle] = useState<BottleData | null>(null);
  const [hydrationEvents, setHydrationEvents] = useState<HydrationEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const notificationCleanup = useRef<(() => void) | null>(null);
  const resolvedUUIDs = useRef<{ service: string; write: string; notify: string }>({
    service: WELLEBIT_SERVICE_UUID,
    write: WELLEBIT_WRITE_CHARACTERISTIC_UUID,
    notify: WELLEBIT_NOTIFY_CHARACTERISTIC_UUID,
  });
  const initAttempted = useRef(false);

  // Initialize BLE
  const initialize = useCallback(async () => {
    try {
      // Check if we're in a native Capacitor environment
      const isNative = typeof (window as any).Capacitor !== 'undefined';
      
      if (!isNative) {
        // In browser, BLE won't work - set friendly error
        setError('native_required');
        setIsInitialized(false);
        return;
      }
      
      await BleClient.initialize();
      setIsInitialized(true);
      
      // Try to reconnect to previously paired device
      const { value } = await Preferences.get({ key: 'wellebit_paired_device' });
      if (value) {
        const savedDevice = JSON.parse(value);
        try {
          await connectToBottle(savedDevice.deviceId, savedDevice.name);
        } catch {
          console.log('Could not reconnect to saved device');
        }
      }
    } catch (err) {
      setError('native_required');
      console.error('BLE init error:', err);
    }
  }, []);

  // Auto-initialize BLE on mount
  useEffect(() => {
    if (!initAttempted.current) {
      initAttempted.current = true;
      console.log('[BLE] Auto-initializing...');
      initialize();
    }
  }, [initialize]);

  // Scan for Wellebit bottles using requestDevice (native picker - most reliable on iOS)
  const scanForBottles = useCallback(async () => {
    // Ensure BLE is initialized
    if (!isInitialized) {
      console.log('[BLE] Not initialized yet, attempting init before scan...');
      try {
        await BleClient.initialize();
        setIsInitialized(true);
        console.log('[BLE] Late init succeeded');
      } catch (initErr) {
        console.error('[BLE] Late init failed:', initErr);
        setError('Bluetooth not available. Make sure Bluetooth is enabled in Settings.');
        return;
      }
    }

    setIsScanning(true);
    setDiscoveredDevices([]);
    setError(null);

    try {
      console.log('[BLE] Opening native device picker via requestDevice...');
      
      // Use requestDevice which shows a native Bluetooth picker dialog
      // Filter by known Wellebit bottle name prefixes to reduce clutter
      const device = await BleClient.requestDevice({
        // Filter to only show Wellebit / G1100 bottles
        namePrefix: 'G1',
        optionalServices: [
          WELLEBIT_SERVICE_UUID,
          ...ALTERNATE_SERVICE_UUIDS,
        ],
      });

      console.log('[BLE] User selected device:', device.name || '(unnamed)', device.deviceId);
      
      setDiscoveredDevices([device]);
      setIsScanning(false);
      
      // Auto-connect to the selected device
      await connectToBottle(device.deviceId, device.name || 'Wellebit Smart Water Bottle');
      
      return device;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[BLE] Device selection error:', msg, err);
      
      if (msg.includes('cancel') || msg.includes('Cancel')) {
        setError('Device selection was cancelled.');
      } else {
        setError(`Scan failed: ${msg}`);
      }
      setIsScanning(false);
      return null;
    }
  }, [isInitialized]);

  // Connect to a specific bottle
  const connectToBottle = useCallback(async (deviceId: string, deviceName?: string) => {
    try {
      setError(null);
      console.log('[BLE] Attempting to connect to:', deviceId, deviceName);

      // On some Android devices, keeping an active scan running can make GATT connections flaky.
      // Stop scanning before attempting to connect.
      try {
        await BleClient.stopLEScan();
      } catch {
        // Ignore (may not be scanning)
      }
      setIsScanning(false);
      
      await BleClient.connect(deviceId, (disconnectedDeviceId) => {
        if (disconnectedDeviceId === deviceId) {
          setConnectedBottle(null);
          console.log('[BLE] Bottle disconnected');
        }
      });

      console.log('[BLE] Connected successfully to:', deviceId);
      
      // Haptic feedback on successful connection
      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch (hapticErr) {
        console.log('[BLE] Haptic feedback unavailable');
      }

      // Discover services to find the correct UUIDs
      console.log('[BLE] Discovering services...');
      let discoveredServices: DiscoveredService[] = [];
      let resolvedServiceUUID = WELLEBIT_SERVICE_UUID;
      let resolvedWriteUUID = WELLEBIT_WRITE_CHARACTERISTIC_UUID;
      let resolvedNotifyUUID = WELLEBIT_NOTIFY_CHARACTERISTIC_UUID;
      try {
        const services = await BleClient.getServices(deviceId);
        discoveredServices = services.map(s => ({
          uuid: s.uuid,
          characteristics: s.characteristics?.map(c => c.uuid) || [],
          characteristicProperties: s.characteristics?.reduce((acc, c) => {
            acc[c.uuid] = {
              write: !!(c.properties?.write || c.properties?.writeWithoutResponse),
              notify: !!(c.properties?.notify || c.properties?.indicate),
              read: !!c.properties?.read,
            };
            return acc;
          }, {} as Record<string, { write?: boolean; notify?: boolean; read?: boolean }>) || {},
        }));
        console.log('[BLE] Discovered services:', JSON.stringify(discoveredServices));

        // Try to resolve the actual service/characteristic UUIDs from discovered services
        const allCandidateUUIDs = [WELLEBIT_SERVICE_UUID, ...ALTERNATE_SERVICE_UUIDS];
        for (const candidateUUID of allCandidateUUIDs) {
          const matchedService = discoveredServices.find(s => s.uuid.toLowerCase() === candidateUUID.toLowerCase());
          if (matchedService && matchedService.characteristics.length >= 2) {
            resolvedServiceUUID = matchedService.uuid;
            
            // Use characteristic properties to find write and notify chars
            const props = matchedService.characteristicProperties || {};
            const writeChar = matchedService.characteristics.find(c => props[c]?.write);
            const notifyChar = matchedService.characteristics.find(c => props[c]?.notify);
            
            if (writeChar && notifyChar) {
              resolvedWriteUUID = writeChar;
              resolvedNotifyUUID = notifyChar;
              console.log('[BLE] Resolved UUIDs via properties:', { resolvedServiceUUID, resolvedWriteUUID, resolvedNotifyUUID });
            } else {
              // Fallback: first writable char, second notify char
              resolvedWriteUUID = matchedService.characteristics[0];
              resolvedNotifyUUID = matchedService.characteristics[1];
              console.log('[BLE] Resolved UUIDs via fallback order:', { resolvedServiceUUID, resolvedWriteUUID, resolvedNotifyUUID });
            }
            break;
          }
        }
      } catch (discoverErr) {
        console.log('[BLE] Service discovery failed:', discoverErr);
      }

      // Store resolved UUIDs for this device
      resolvedUUIDs.current = { service: resolvedServiceUUID, write: resolvedWriteUUID, notify: resolvedNotifyUUID };

      // Initialize bottle data with defaults
      const bottleData: BottleData = {
        deviceId,
        deviceName: deviceName || 'Wellebit Smart Water Bottle',
        connected: true,
        batteryLevel: null,
        batteryFull: false,
        lowBattery: false,
        waterTemperature: 20,
        currentWaterVolume: 0,
        lastDrinkAmount: 0,
        totalIntakeToday: 0,
        sterilisationOn: false,
        sterilisationDuration: 0,
        drinkingLidOpen: false,
        refillLidOpen: false,
        isTilted: false,
        softwareVersion: 0,
        lastSync: new Date(),
        discoveredServices,
      };

      setConnectedBottle(bottleData);

      // Save paired device for auto-reconnect
      await Preferences.set({
        key: 'wellebit_paired_device',
        value: JSON.stringify({ deviceId, name: deviceName })
      });

      // Try to start notifications using resolved UUIDs
      try {
        await startNotifications(deviceId);
        console.log('[BLE] Notifications started');
      } catch (notifyErr) {
        console.log('[BLE] Could not start notifications:', notifyErr);
      }
      
      // Try to set system time
      try {
        await setSystemTime(deviceId);
        console.log('[BLE] System time set');
      } catch (timeErr) {
        console.log('[BLE] Could not set system time:', timeErr);
      }
      
      // Try to request initial status
      try {
        await requestAllStatus(deviceId);
        console.log('[BLE] Status requested');
      } catch (statusErr) {
        console.log('[BLE] Could not request status:', statusErr);
      }

      return bottleData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('[BLE] Connect error:', errorMessage, err);
      setError(`Failed to connect: ${errorMessage}`);
      throw err;
    }
  }, []);

  // Send a command to the bottle
  const sendCommand = useCallback(async (deviceId: string, commandCode: number, data: number[] = []) => {
    try {
      const frame = buildCommandFrame(commandCode, data);
      const dataView = new DataView(frame.buffer);
      await BleClient.write(
        deviceId,
        resolvedUUIDs.current.service,
        resolvedUUIDs.current.write,
        dataView
      );
    } catch (err) {
      console.error('Send command error:', err);
    }
  }, []);

  // Set system time (10-digit Unix timestamp)
  const setSystemTime = useCallback(async (deviceId: string) => {
    const timestamp = Math.floor(Date.now() / 1000);
    // Convert to little-endian 4 bytes
    const data = [
      timestamp & 0xFF,
      (timestamp >> 8) & 0xFF,
      (timestamp >> 16) & 0xFF,
      (timestamp >> 24) & 0xFF
    ];
    await sendCommand(deviceId, COMMANDS.SET_SYSTEM_TIME, data);
  }, [sendCommand]);

  // Request all status from bottle
  const requestAllStatus = useCallback(async (deviceId: string) => {
    // Request all status values
    await sendCommand(deviceId, COMMANDS.REQUEST_BATTERY_LEVEL);
    await sendCommand(deviceId, COMMANDS.REQUEST_BATTERY_FULL);
    await sendCommand(deviceId, COMMANDS.REQUEST_LOW_BATTERY);
    await sendCommand(deviceId, COMMANDS.REQUEST_TEMPERATURE);
    await sendCommand(deviceId, COMMANDS.REQUEST_CURRENT_WATER_VOLUME);
    await sendCommand(deviceId, COMMANDS.REQUEST_STERILISATION_STATUS);
    await sendCommand(deviceId, COMMANDS.REQUEST_STERILISATION_DURATION);
    await sendCommand(deviceId, COMMANDS.REQUEST_DRINKING_LID_STATUS);
    await sendCommand(deviceId, COMMANDS.REQUEST_REFILL_LID_STATUS);
    await sendCommand(deviceId, COMMANDS.REQUEST_ORIENTATION);
    await sendCommand(deviceId, COMMANDS.REQUEST_SOFTWARE_VERSION);
  }, [sendCommand]);

  // Handle MCU response
  const handleMCUResponse = useCallback((command: number, payload: number[]) => {
    setConnectedBottle(prev => {
      if (!prev) return null;
      
      const updated = { ...prev, lastSync: new Date() };
      
      switch (command) {
        case COMMANDS.RESPONSE_CHANGED_WATER_VOLUME: {
          // Water volume in 0.01L units (10ml)
          const volumeUnits = parseUint16LE(payload);
          const amountMl = volumeUnits * 10;
          updated.lastDrinkAmount = amountMl;
          updated.totalIntakeToday += amountMl;
          
          console.log('[BLE] 🥤 DRINK DETECTED!', amountMl, 'ml — Total today:', updated.totalIntakeToday, 'ml');
          
          // Create hydration event
          const event: HydrationEvent = {
            timestamp: new Date(),
            amountMl,
            temperature: prev.waterTemperature
          };
          setHydrationEvents(events => [...events, event]);
          
          // Haptic feedback for drink event
          Haptics.impact({ style: ImpactStyle.Light });
          break;
        }
        case COMMANDS.RESPONSE_CURRENT_WATER_VOLUME: {
          const volumeUnits = parseUint16LE(payload);
          updated.currentWaterVolume = volumeUnits * 10; // Convert to ml
          break;
        }
        case COMMANDS.RESPONSE_STERILISATION_STATUS:
          updated.sterilisationOn = payload[0] === 1;
          break;
        case COMMANDS.RESPONSE_STERILISATION_DURATION: {
          const seconds = parseUint16LE(payload);
          updated.sterilisationDuration = seconds;
          break;
        }
        case COMMANDS.RESPONSE_TEMPERATURE:
          updated.waterTemperature = payload[0]; // °C
          break;
        case COMMANDS.RESPONSE_DRINKING_LID_STATUS:
          updated.drinkingLidOpen = payload[0] === 1;
          break;
        case COMMANDS.RESPONSE_REFILL_LID_STATUS:
          updated.refillLidOpen = payload[0] === 1;
          break;
        case COMMANDS.RESPONSE_ORIENTATION:
          updated.isTilted = payload[0] === 1;
          break;
        case COMMANDS.RESPONSE_BATTERY_LEVEL:
          updated.batteryLevel = normalizeBatteryLevel(payload[0]);
          break;
        case COMMANDS.RESPONSE_BATTERY_FULL:
          updated.batteryFull = payload[0] === 1;
          break;
        case COMMANDS.RESPONSE_LOW_BATTERY:
          updated.lowBattery = payload[0] === 1;
          break;
        case COMMANDS.RESPONSE_SOFTWARE_VERSION:
          updated.softwareVersion = parseUint16LE(payload);
          break;
      }
      
      return updated;
    });
  }, []);

  // Start listening for MCU notifications
  const startNotifications = useCallback(async (deviceId: string) => {
    try {
      await BleClient.startNotifications(
        deviceId,
        resolvedUUIDs.current.service,
        resolvedUUIDs.current.notify,
        (value) => {
          const dataView = new DataView(value.buffer);
          const raw = Array.from(new Uint8Array(value.buffer));
          console.log('[BLE] Notification received, raw bytes:', raw.map(b => '0x' + b.toString(16).padStart(2, '0')).join(' '));
          const parsed = parseResponseFrame(dataView);
          
          if (parsed) {
            console.log('[BLE] Parsed command:', '0x' + parsed.command.toString(16), 'payload:', parsed.payload);
            handleMCUResponse(parsed.command, parsed.payload);
          } else {
            console.warn('[BLE] Could not parse notification frame');
          }
        }
      );

      notificationCleanup.current = () => {
        BleClient.stopNotifications(
          deviceId,
          resolvedUUIDs.current.service,
          resolvedUUIDs.current.notify
        );
      };
    } catch (err) {
      console.error('Notification setup error:', err);
    }
  }, [handleMCUResponse]);

  // Disconnect from bottle
  const disconnect = useCallback(async () => {
    if (!connectedBottle) return;

    try {
      if (notificationCleanup.current) {
        notificationCleanup.current();
        notificationCleanup.current = null;
      }
      
      await BleClient.disconnect(connectedBottle.deviceId);
      setConnectedBottle(null);
      
      await Preferences.remove({ key: 'wellebit_paired_device' });
    } catch (err) {
      console.error('Disconnect error:', err);
    }
  }, [connectedBottle]);

  // Manual sync - request current data from bottle
  const syncBottle = useCallback(async () => {
    if (!connectedBottle) return;

    try {
      await requestAllStatus(connectedBottle.deviceId);
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (err) {
      setError('Failed to sync bottle data');
      console.error('Sync error:', err);
    }
  }, [connectedBottle, requestAllStatus]);

  // Toggle sterilisation
  const toggleSterilisation = useCallback(async (enable: boolean) => {
    if (!connectedBottle) return;
    await sendCommand(connectedBottle.deviceId, COMMANDS.SET_MANUAL_STERILISATION, [enable ? 1 : 0]);
  }, [connectedBottle, sendCommand]);

  // Set drink reminder (TBD in protocol)
  const setDrinkReminder = useCallback(async (intervalMinutes: number) => {
    if (!connectedBottle) return;
    const data = [intervalMinutes & 0xFF, (intervalMinutes >> 8) & 0xFF];
    await sendCommand(connectedBottle.deviceId, COMMANDS.SET_DRINK_REMINDER, data);
  }, [connectedBottle, sendCommand]);

  // Set timed vibration (TBD in protocol)
  const setTimedVibration = useCallback(async (enabled: boolean, intervalMinutes: number) => {
    if (!connectedBottle) return;
    const data = [enabled ? 1 : 0, intervalMinutes & 0xFF, (intervalMinutes >> 8) & 0xFF];
    await sendCommand(connectedBottle.deviceId, COMMANDS.SET_TIMED_VIBRATION, data);
  }, [connectedBottle, sendCommand]);

  // Clear today's hydration data (for new day)
  const resetDailyIntake = useCallback(() => {
    setConnectedBottle(prev => prev ? {
      ...prev,
      totalIntakeToday: 0
    } : null);
    setHydrationEvents([]);
  }, []);

  // Initialize on mount
  useEffect(() => {
    initialize();
    
    return () => {
      if (notificationCleanup.current) {
        notificationCleanup.current();
      }
    };
  }, [initialize]);

  return {
    isInitialized,
    isScanning,
    discoveredDevices,
    connectedBottle,
    hydrationEvents,
    error,
    scanForBottles,
    connectToBottle,
    disconnect,
    syncBottle,
    resetDailyIntake,
    toggleSterilisation,
    setDrinkReminder,
    setTimedVibration,
    clearError: () => setError(null)
  };
}
